// CustomPathBuilder — Service toggle component for "Something else" project type.
// MANUS-WIZARD-V2 Task 4 (AC-24 through AC-27).
// Lets the user manually select which services to include in their wizard path.
// GitHub and Deploy & Verify are always enabled (AC-25).
// Live step count updates as toggles change (AC-26).
// Builds a WizardStep[] from toggle selections (AC-27).

import { useState, useMemo, useEffect } from 'react';
import type { WizardStep } from '../../lib/path-matrix';
import { STEP_LABELS } from '../../lib/path-matrix';

/** Services the user can toggle on/off. */
const TOGGLEABLE_SERVICES: { id: WizardStep; label: string; desc: string }[] = [
  { id: 'railway', label: 'Railway', desc: 'Backend server deployment' },
  { id: 'supabase', label: 'Supabase', desc: 'Managed Postgres database' },
  { id: 'netlify', label: 'Netlify', desc: 'Frontend / static hosting' },
  { id: 'mailgun', label: 'Mailgun', desc: 'Transactional email' },
  { id: 'stripe', label: 'Stripe', desc: 'Payment processing' },
];

/** Locked services — always included, cannot be toggled off (AC-25). */
const LOCKED_SERVICES: { id: WizardStep; label: string; desc: string }[] = [
  { id: 'github', label: 'GitHub', desc: 'Repository & CI — always required' },
  { id: 'deploy_verify', label: 'Deploy & Verify', desc: 'Final deployment check — always required' },
];

interface CustomPathBuilderProps {
  /** Pre-selected services from Claude classification (may be empty). */
  suggestedServices?: string[];
  /** Claude's reasoning text (shown as a hint). */
  reasoning?: string;
  /** Called whenever the user changes toggles — provides the full step path. */
  onPathChange: (path: WizardStep[]) => void;
}

export default function CustomPathBuilder({
  suggestedServices = [],
  reasoning,
  onPathChange,
}: CustomPathBuilderProps) {
  // Initialise toggles from suggested services
  const [enabled, setEnabled] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    for (const svc of TOGGLEABLE_SERVICES) {
      init[svc.id] = suggestedServices.includes(svc.id);
    }
    return init;
  });

  // Build the path from current toggles
  const path = useMemo<WizardStep[]>(() => {
    const middle = TOGGLEABLE_SERVICES
      .filter(svc => enabled[svc.id])
      .map(svc => svc.id);
    return ['github', ...middle, 'deploy_verify'];
  }, [enabled]);

  // Notify parent whenever path changes
  useEffect(() => {
    onPathChange(path);
  }, [path, onPathChange]);

  const toggle = (id: string) => {
    setEnabled(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const stepCount = path.length;

  return (
    <div style={{ marginTop: 4 }}>
      {/* Reasoning hint from Claude */}
      {reasoning && (
        <div style={{
          marginBottom: 16,
          padding: '10px 14px',
          background: 'var(--blue-bg, rgba(59,130,246,0.08))',
          border: '1px solid var(--blue, #3b82f6)',
          borderRadius: 8,
          fontSize: 13,
          color: 'var(--text)',
          lineHeight: 1.5,
        }}>
          <strong>AI suggestion:</strong> {reasoning}
        </div>
      )}

      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>
        Select the services you need:
      </div>

      {/* Locked services (always on) */}
      {LOCKED_SERVICES.map(svc => (
        <label
          key={svc.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            marginBottom: 6,
            border: '1px solid var(--blue, #3b82f6)',
            borderRadius: 8,
            background: 'var(--blue-bg, rgba(59,130,246,0.08))',
            opacity: 0.7,
            cursor: 'not-allowed',
          }}
        >
          <input type="checkbox" checked disabled style={{ accentColor: 'var(--blue)' }} />
          <div>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{svc.label}</span>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 8 }}>{svc.desc}</span>
          </div>
        </label>
      ))}

      {/* Toggleable services */}
      {TOGGLEABLE_SERVICES.map(svc => {
        const isOn = enabled[svc.id];
        return (
          <label
            key={svc.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 14px',
              marginBottom: 6,
              border: `1px solid ${isOn ? 'var(--blue, #3b82f6)' : 'var(--border)'}`,
              borderRadius: 8,
              background: isOn ? 'var(--blue-bg, rgba(59,130,246,0.08))' : 'var(--surface)',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
            onClick={(e) => { e.preventDefault(); toggle(svc.id); }}
          >
            <input
              type="checkbox"
              checked={isOn}
              onChange={() => toggle(svc.id)}
              style={{ accentColor: 'var(--blue)' }}
            />
            <div>
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{svc.label}</span>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 8 }}>{svc.desc}</span>
            </div>
          </label>
        );
      })}

      {/* Live step count (AC-26) */}
      <div style={{
        marginTop: 16,
        padding: '12px 16px',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 10,
      }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
          {stepCount} setup step{stepCount !== 1 ? 's' : ''}:
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {path.map((stepId, i) => (
            <span
              key={i}
              style={{
                display: 'inline-block',
                padding: '4px 10px',
                fontSize: 12,
                fontWeight: 500,
                borderRadius: 6,
                background: 'var(--blue-bg, rgba(59,130,246,0.08))',
                color: 'var(--blue, #3b82f6)',
                border: '1px solid var(--blue, #3b82f6)',
              }}
            >
              {STEP_LABELS[stepId]}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
